import React, { useState, useEffect } from 'react';
import { X, Plus, Calculator, FileText, Search } from 'lucide-react';
import { ProformaInvoiceFormData, ProformaInvoiceItem, VAT_RATES } from '../../types/proformaInvoice';
import { validateNIP } from '../../utils/generateProformaInvoicePDF';
import { fetchCompanyData } from '../../api/gus';

interface ProformaInvoiceFormProps {
  onSubmit: (data: ProformaInvoiceFormData) => void;
  onCancel: () => void;
  initialData?: {
    buyerName?: string;
    items?: Array<{ name: string; quantity: number; unit: string }>;
  };
  qualityCheckId?: string;
  qualityControlId?: string;
}

export const ProformaInvoiceForm: React.FC<ProformaInvoiceFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  qualityCheckId,
  qualityControlId
}) => {
  const effectiveQualityId = qualityCheckId || qualityControlId;

  // Form state
  const [formData, setFormData] = useState<ProformaInvoiceFormData>({
    issueDate: new Date().toISOString().split('T')[0],
    saleDate: new Date().toISOString().split('T')[0],
    paymentDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    paymentMethod: 'transfer',
    clientName: initialData?.buyerName || '',
    clientAddress: '',
    clientPostalCode: '',
    clientCity: '',
    clientNIP: '',
    clientEmail: '',
    clientPhone: '',
    items: initialData?.items?.map(item => ({
      description: item.name,
      quantity: item.quantity,
      unit: item.unit,
      unitPrice: 0,
      vatRate: 23,
      netAmount: 0,
      vatAmount: 0,
      grossAmount: 0
    })) || [{
      description: '',
      quantity: 1,
      unit: 'szt.',
      unitPrice: 0,
      vatRate: 23,
      netAmount: 0,
      vatAmount: 0,
      grossAmount: 0
    }],
    notes: '',
    internalNotes: '',
    qualityControlId: effectiveQualityId
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingGUS, setLoadingGUS] = useState(false);

  // Funkcja do pobierania danych z GUS
  const fetchFromGUS = async () => {
    if (!formData.clientNIP) {
      setErrors({ ...errors, clientNIP: 'Wprowadź NIP' });
      return;
    }

    setLoadingGUS(true);
    try {
      const result = await fetchCompanyData(formData.clientNIP);
      if (result) {
        // Rozdziel adres na części
        const addressParts = result.address.match(/(.*?)(\d{2}-\d{3})\s+(.*)/);
        let street = result.address;
        let postalCode = '';
        let city = '';
        
        if (addressParts) {
          street = addressParts[1].trim();
          postalCode = addressParts[2];
          city = addressParts[3];
        }
        
        setFormData({
          ...formData,
          clientName: result.name,
          clientAddress: street,
          clientPostalCode: postalCode,
          clientCity: city,
          // Pozostaw NIP bez zmian
        });
        // Wyczyść błędy
        setErrors({});
      } else {
        setErrors({ ...errors, clientNIP: 'Nie znaleziono firmy w GUS' });
      }
    } catch (error) {
      setErrors({ ...errors, clientNIP: 'Błąd pobierania danych z GUS' });
    } finally {
      setLoadingGUS(false);
    }
  };

  // Calculate item values
  const calculateItemValues = (items: typeof formData.items) => {
    return items.map(item => {
      const netAmount = item.quantity * item.unitPrice;
      const vatAmount = item.vatRate === -1 ? 0 : (netAmount * item.vatRate) / 100;
      const grossAmount = netAmount + vatAmount;
      
      return {
        ...item,
        netAmount,
        vatAmount,
        grossAmount
      };
    });
  };

  // Update item
  const updateItem = (index: number, field: keyof ProformaInvoiceItem, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    
    // Recalculate values if quantity, price or VAT rate changed
    if (field === 'quantity' || field === 'unitPrice' || field === 'vatRate') {
      const calculatedItems = calculateItemValues(newItems);
      setFormData({ ...formData, items: calculatedItems });
    } else {
      setFormData({ ...formData, items: newItems });
    }
  };

  // Add item
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        description: '',
        quantity: 1,
        unit: 'szt.',
        unitPrice: 0,
        vatRate: 23,
        netAmount: 0,
        vatAmount: 0,
        grossAmount: 0
      }]
    });
  };

  // Remove item
  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  // Calculate summary
  const summary = {
    netTotal: formData.items.reduce((sum, item) => sum + (item.netAmount || 0), 0),
    vatTotal: formData.items.reduce((sum, item) => sum + (item.vatAmount || 0), 0),
    grossTotal: 0
  };
  summary.grossTotal = summary.netTotal + summary.vatTotal;

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientName) newErrors.clientName = 'Nazwa jest wymagana';
    if (!formData.clientAddress) newErrors.clientAddress = 'Adres jest wymagany';
    if (!formData.clientPostalCode) newErrors.clientPostalCode = 'Kod pocztowy jest wymagany';
    if (!formData.clientCity) newErrors.clientCity = 'Miasto jest wymagane';
    
    if (formData.clientPostalCode && !/^\d{2}-\d{3}$/.test(formData.clientPostalCode)) {
      newErrors.clientPostalCode = 'Format: 00-000';
    }
    
    if (formData.clientNIP && !validateNIP(formData.clientNIP)) {
      newErrors.clientNIP = 'Nieprawidłowy NIP';
    }
    
    if (formData.clientEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.clientEmail)) {
      newErrors.clientEmail = 'Nieprawidłowy adres email';
    }

    // Validate items
    formData.items.forEach((item, index) => {
      if (!item.description) newErrors[`item_${index}_description`] = 'Nazwa jest wymagana';
      if (item.quantity <= 0) newErrors[`item_${index}_quantity`] = 'Ilość musi być większa od 0';
      if (item.unitPrice < 0) newErrors[`item_${index}_unitPrice`] = 'Cena nie może być ujemna';
      if (!item.unit) newErrors[`item_${index}_unit`] = 'Jednostka jest wymagana';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="bg-zinc-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-orange-500" />
          <h2 className="text-xl font-semibold">Nowa faktura proforma</h2>
          {effectiveQualityId && (
            <span className="text-sm text-zinc-400">
              (dla kontroli jakości)
            </span>
          )}
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-zinc-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dane nabywcy */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Dane nabywcy</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Nazwa firmy *
              </label>
              <input
                value={formData.clientName}
                onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
              />
              {errors.clientName && (
                <p className="text-red-500 text-sm mt-1">{errors.clientName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                NIP
              </label>
              <div className="flex gap-2">
                <input
                  value={formData.clientNIP}
                  onChange={(e) => setFormData({ ...formData, clientNIP: e.target.value })}
                  placeholder="123-456-78-90"
                  className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
                />
                <button
                  type="button"
                  onClick={fetchFromGUS}
                  disabled={loadingGUS}
                  className="px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-600/50 rounded-lg transition-colors flex items-center gap-2"
                  title="Pobierz dane z GUS"
                >
                  <Search className="w-4 h-4" />
                  {loadingGUS ? 'Szukam...' : 'GUS'}
                </button>
              </div>
              {errors.clientNIP && (
                <p className="text-red-500 text-sm mt-1">{errors.clientNIP}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Adres *
              </label>
              <input
                value={formData.clientAddress}
                onChange={(e) => setFormData({ ...formData, clientAddress: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
              />
              {errors.clientAddress && (
                <p className="text-red-500 text-sm mt-1">{errors.clientAddress}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Kod pocztowy *
                </label>
                <input
                  value={formData.clientPostalCode}
                  onChange={(e) => setFormData({ ...formData, clientPostalCode: e.target.value })}
                  placeholder="00-000"
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
                />
                {errors.clientPostalCode && (
                  <p className="text-red-500 text-sm mt-1">{errors.clientPostalCode}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Miasto *
                </label>
                <input
                  value={formData.clientCity}
                  onChange={(e) => setFormData({ ...formData, clientCity: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
                />
                {errors.clientCity && (
                  <p className="text-red-500 text-sm mt-1">{errors.clientCity}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.clientEmail}
                onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
              />
              {errors.clientEmail && (
                <p className="text-red-500 text-sm mt-1">{errors.clientEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Telefon
              </label>
              <input
                value={formData.clientPhone}
                onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>
        </div>

        {/* Pozycje faktury */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Pozycje faktury</h3>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-2 px-3 py-1 bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Dodaj pozycję
            </button>
          </div>

          <div className="space-y-3">
            {formData.items.map((item, index) => (
              <div key={index} className="bg-zinc-700 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 flex-1">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-zinc-300 mb-1">
                        Nazwa
                      </label>
                      <input
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                      />
                      {errors[`item_${index}_description`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_description`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">
                        Ilość
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                      />
                      {errors[`item_${index}_quantity`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_quantity`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1">
                        Jedn.
                      </label>
                      <input
                        value={item.unit}
                        onChange={(e) => updateItem(index, 'unit', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                      />
                      {errors[`item_${index}_unit`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_unit`]}</p>
                      )}
                    </div>
                  </div>

                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="ml-2 p-1 text-zinc-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">
                      Cena netto
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                    />
                    {errors[`item_${index}_unitPrice`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_unitPrice`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">
                      VAT
                    </label>
                    <select
                      value={item.vatRate}
                      onChange={(e) => updateItem(index, 'vatRate', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
                    >
                      {VAT_RATES.map(rate => (
                        <option key={rate.value} value={rate.value}>
                          {rate.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">
                      Netto
                    </label>
                    <div className="px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm">
                      {item.netAmount.toFixed(2)} zł
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">
                      VAT
                    </label>
                    <div className="px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm">
                      {item.vatAmount.toFixed(2)} zł
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">
                      Brutto
                    </label>
                    <div className="px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm font-medium">
                      {item.grossAmount.toFixed(2)} zł
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Podsumowanie */}
        <div className="bg-zinc-700 rounded-lg p-4">
          <h3 className="text-lg font-medium mb-3">Podsumowanie</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-zinc-400">Razem netto:</span>
              <p className="text-xl font-semibold">{summary.netTotal.toFixed(2)} zł</p>
            </div>
            <div>
              <span className="text-sm text-zinc-400">VAT:</span>
              <p className="text-xl font-semibold">{summary.vatTotal.toFixed(2)} zł</p>
            </div>
            <div>
              <span className="text-sm text-zinc-400">Do zapłaty:</span>
              <p className="text-2xl font-bold text-orange-500">{summary.grossTotal.toFixed(2)} zł</p>
            </div>
          </div>
        </div>

        {/* Warunki płatności */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">
              Sposób płatności
            </label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
            >
              <option value="transfer">Przelew</option>
              <option value="cash">Gotówka</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">
              Termin płatności
            </label>
            <input
              type="date"
              value={formData.paymentDeadline}
              onChange={(e) => setFormData({ ...formData, paymentDeadline: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
            />
          </div>
        </div>

        {/* Uwagi */}
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-1">
            Uwagi / Notatki
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:border-orange-500"
            placeholder="Dodatkowe informacje..."
          />
        </div>

        {/* Przyciski */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
          >
            Anuluj
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Wystaw fakturę proforma
          </button>
        </div>
      </form>
    </div>
  );
};
